import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, type OrderList, type OrderSummary } from "@/lib/api";
import { formatDate, formatMoney } from "../format";
import DocumentButtons from "./DocumentButtons";
import StatusBadge from "./StatusBadge";

const PAGE_SIZE = 10;

/** The signed-in customer's credit purchases, newest first, on the profile page. */
export default function OrderHistory() {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (nextPage: number) => {
      setLoading(true);
      setError(null);
      try {
        const list = await api.get<OrderList>(`/orders?page=${nextPage}&pageSize=${PAGE_SIZE}`);
        setOrders((current) => (nextPage === 1 ? list.orders : [...current, ...list.orders]));
        setTotal(list.total);
        setPage(nextPage);
      } catch (err) {
        setError(err instanceof Error ? err.message : t("orders.history.loadFailed"));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  return (
    <section className="bg-background-100 border border-background-200/70 rounded-lg p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-heading font-bold text-base text-foreground-950">{t("orders.history.title")}</h2>
          <p className="mt-1 text-sm text-foreground-500">{t("orders.history.subtitle")}</p>
        </div>
        {total > 0 && (
          <span className="text-xs text-foreground-500 whitespace-nowrap">
            {t("orders.history.count", { count: total })}
          </span>
        )}
      </div>

      {error && <div className="mt-4 rounded-md bg-red-100 text-red-700 px-4 py-3 text-sm">{error}</div>}

      {!loading && !error && orders.length === 0 && (
        <div className="mt-6 text-center py-8">
          <div className="w-12 h-12 mx-auto rounded-full bg-background-200/70 flex items-center justify-center">
            <i className="ri-shopping-bag-3-line text-xl text-foreground-400"></i>
          </div>
          <p className="mt-3 text-sm font-medium text-foreground-900">{t("orders.history.empty")}</p>
          <p className="mt-1 text-sm text-foreground-500">{t("orders.history.emptyBody")}</p>
          <Link
            to="/pricing"
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-add-circle-line"></i>
            {t("profile.stats.buyMore")}
          </Link>
        </div>
      )}

      {orders.length > 0 && (
        <ul className="mt-5 divide-y divide-background-200/70 border-y border-background-200/70">
          {orders.map((order) => (
            <li key={order.id} className="flex items-center gap-3 py-3">
              <Link to={`/orders/${order.id}`} className="flex-1 min-w-0 group">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-semibold text-sm text-foreground-950 group-hover:text-primary-600 transition-colors">
                    {t("orders.planLabel", { plan: order.planName })}
                  </span>
                  <StatusBadge status={order.status} />
                </div>
                <div className="mt-1 text-xs text-foreground-500 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>{t("orders.orderNumber", { id: order.id })}</span>
                  <span>{formatDate(order.createdAt)}</span>
                  {order.credits !== null && (
                    <span>
                      <i className="ri-coins-line mr-0.5"></i>
                      {t("orders.creditsCount", { count: order.credits })}
                    </span>
                  )}
                </div>
              </Link>
              <div className="text-right shrink-0">
                <div className="font-semibold text-sm text-foreground-950">
                  {formatMoney(order.amount, order.currency)}
                </div>
              </div>
              <div className="shrink-0 w-[72px] flex justify-end">
                {order.status === "paid" ? (
                  <DocumentButtons orderId={order.id} compact onError={setError} />
                ) : (
                  <Link
                    to={`/orders/${order.id}`}
                    aria-label={t("orders.history.viewDetails")}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-200/70 hover:text-primary-600 transition-colors"
                  >
                    <i className="ri-arrow-right-s-line text-lg"></i>
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {loading && (
        <div className="mt-6 flex justify-center">
          <div className="w-8 h-8 rounded-full loader-ring"></div>
        </div>
      )}

      {!loading && orders.length < total && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => load(page + 1)}
            className="whitespace-nowrap rounded-md border border-background-300/60 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-900 hover:bg-background-200/50 transition-colors cursor-pointer"
          >
            {t("orders.history.loadMore")}
          </button>
        </div>
      )}
    </section>
  );
}
